import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import { weatherService, WeatherConfig } from '../../services/weatherService';

interface WeatherSettingsProps {
  open: boolean;
  onClose: () => void;
  onConfigChange: () => void;
}

const WeatherSettings: React.FC<WeatherSettingsProps> = ({
  open,
  onClose,
  onConfigChange
}) => {
  const [config, setConfig] = useState<WeatherConfig>(weatherService.getConfig());

  useEffect(() => {
    if (open) {
      setConfig(weatherService.getConfig());
    }
  }, [open]);

  const handleSave = () => {
    weatherService.setConfig(config);
    onConfigChange();
    onClose();
  };

  const handleCancel = () => {
    setConfig(weatherService.getConfig());
    onClose();
  };

  const handleConfigChange = (field: keyof WeatherConfig, value: any) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Weather Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          Using the free National Weather Service API - no API key required!
        </Alert>

          <TextField
            label="City"
            value={config.city}
            onChange={(e) => handleConfigChange('city', e.target.value)}
            fullWidth
            placeholder="e.g., New York, London, Tokyo"
            helperText="City name for weather data (fallback if zip code not available)"
          />

          <TextField
            label="ZIP Code"
            value={config.zipCode}
            onChange={(e) => handleConfigChange('zipCode', e.target.value)}
            fullWidth
            placeholder="e.g., 10001, 90210"
            helperText="ZIP/Postal code for more accurate weather data (preferred)"
          />

          <FormControl fullWidth>
            <InputLabel>Temperature Units</InputLabel>
            <Select
              value={config.units}
              onChange={(e) => handleConfigChange('units', e.target.value)}
              label="Temperature Units"
            >
              <MenuItem value="metric">Celsius (°C)</MenuItem>
              <MenuItem value="imperial">Fahrenheit (°F)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Update Interval (minutes)"
            type="number"
            value={config.updateInterval}
            onChange={(e) => handleConfigChange('updateInterval', parseInt(e.target.value) || 10)}
            fullWidth
            inputProps={{ min: 1, max: 60 }}
            helperText="How often to refresh weather data (1-60 minutes)"
          />

          <Divider />

          <Typography variant="h6" gutterBottom>
            Weather Features
          </Typography>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={true}
                  disabled
                  color="primary"
                />
              }
              label="Weather Overlay in Top Bar"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
              Shows current weather conditions in the top navigation bar
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={true}
                  disabled
                  color="primary"
                />
              }
              label="Animated Background Effects"
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
              Shows weather animations on the page background
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Weather animations will appear for 5 seconds when conditions change.
              The system automatically detects weather changes and triggers appropriate animations.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeatherSettings;
